/**
 * KanbanCard - Individual ticket card component for the kanban board.
 * @implements FS-005.9.2 - Ticket Cards
 * @implements FS-005.9.2.2 - Card Content (title, type badge, AC progress)
 * @implements FS-005.9.2.3 - Type-Based Visual Differentiation
 * @implements FS-005.9.4.3 - Card Movement Animation
 *
 * Features:
 * - Type badge with distinct colors per ticket type
 * - Title with truncation (2-3 lines max)
 * - Acceptance criteria progress indicator
 * - Type-based subtle background tint
 * - Hover state with elevation
 * - Framer Motion animations for enter/exit/layout
 */

import { Box, Typography, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import type { KanbanTicketPayload } from '@/stores/KanbanStore';

/**
 * Type-to-color mapping for ticket type badges.
 * @implements FS-005.9.2.3 - Type-Based Visual Differentiation
 */
const TYPE_BADGE_COLORS: Record<string, string> = {
  MILESTONE: '#9c27b0', // Purple
  EPIC: '#1976d2', // Blue
  USER_STORY: '#2e7d32', // Green
  TECHNICAL_STORY: '#ed6c02', // Orange
};

/**
 * Subtle background tints for card based on ticket type.
 * @implements FS-005.9.2.3 - Type-Based Visual Differentiation
 */
const TYPE_BACKGROUND_TINTS: Record<string, string> = {
  MILESTONE: 'rgba(156, 39, 176, 0.04)', // Purple tint
  EPIC: 'rgba(25, 118, 210, 0.04)', // Blue tint
  USER_STORY: 'rgba(46, 125, 50, 0.04)', // Green tint
  TECHNICAL_STORY: 'rgba(237, 108, 2, 0.04)', // Orange tint
};

/**
 * Human-readable display names for ticket types.
 */
const TYPE_DISPLAY_NAMES: Record<string, string> = {
  MILESTONE: 'Milestone',
  EPIC: 'Epic',
  USER_STORY: 'US',
  TECHNICAL_STORY: 'TS',
};

/**
 * Props for KanbanCard component.
 */
export interface KanbanCardProps {
  /** The ticket data to display */
  ticket: KanbanTicketPayload;
  /** Callback when the card is clicked */
  onClick?: () => void;
}

/**
 * Framer Motion variants for card animations.
 * @implements FS-005.9.4.3 - Card Movement Animation
 */
const cardVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.2,
    },
  },
};

/**
 * KanbanCard - Renders a single ticket card for the kanban board.
 *
 * @implements FS-005.9.2.2 - Card Content (title, type badge, progress)
 * @implements FS-005.9.2.3 - Type-Based Visual Differentiation
 * @implements FS-005.9.4.3 - Card Movement Animation
 *
 * Features:
 * - Type badge with distinct color per type (Milestone=purple, Epic=blue, US=green, TS=orange)
 * - Title truncated to 2-3 lines with ellipsis
 * - Acceptance criteria progress showing completed/total
 * - Subtle type-based background tint
 * - Hover state with elevation increase
 * - Framer Motion animations for smooth enter/exit/layout
 */
export const KanbanCard = observer(({ ticket, onClick }: KanbanCardProps) => {
  const typeColor = TYPE_BADGE_COLORS[ticket.type] || '#757575';
  const bgTint = TYPE_BACKGROUND_TINTS[ticket.type] || 'transparent';
  const typeLabel = TYPE_DISPLAY_NAMES[ticket.type] || ticket.type;

  // Calculate acceptance criteria progress
  // acceptanceCriteria is an array of strings in the current payload
  // When items have completed status, we would count them, but for now
  // we show total count as the data model shows strings
  const totalAC = ticket.acceptanceCriteria?.length || 0;
  // Note: The current KanbanTicketPayload has acceptanceCriteria as string[]
  // When the payload includes completion status, this can be updated
  const completedAC = 0; // Placeholder until completion data is available

  return (
    <motion.div
      layout
      variants={cardVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      layoutId={`kanban-card-${ticket.id}`}
      style={{ width: '100%' }}
    >
      <Box
        onClick={onClick}
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          background: bgTint,
          borderRadius: 1,
          boxShadow: 1,
          cursor: onClick ? 'pointer' : 'default',
          borderLeft: `3px solid ${typeColor}`,
          transition:
            'box-shadow 0.2s ease-in-out, transform 0.15s ease-in-out',
          '&:hover': {
            boxShadow: onClick ? 4 : 1,
            transform: onClick ? 'translateY(-2px)' : 'none',
          },
        }}
        data-testid={`kanban-card-${ticket.id}`}
      >
        {/* Header: Title and Type Badge */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            mb: 1,
          }}
        >
          {/* Title - truncated to 2 lines */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              flex: 1,
              minWidth: 0,
            }}
          >
            {ticket.title}
          </Typography>

          {/* Type Badge */}
          <Chip
            label={typeLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: typeColor,
              color: '#fff',
              flexShrink: 0,
              '& .MuiChip-label': {
                px: 0.75,
              },
            }}
          />
        </Box>

        {/* Footer: Acceptance Criteria Progress */}
        {totalAC > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 14,
                  height: 14,
                  borderRadius: '2px',
                  bgcolor: 'grey.200',
                  color: 'text.secondary',
                  fontSize: '0.6rem',
                }}
              >
                &#x2713;
              </Box>
              {completedAC}/{totalAC}
            </Typography>
          </Box>
        )}
      </Box>
    </motion.div>
  );
});

// Display name for React DevTools
KanbanCard.displayName = 'KanbanCard';
